import { formatBytes } from 'core/services/utils';

import { RecordInfoRendererType } from '../shared';
import { infoRenderer } from './recordHelpers';

function renderInfoCol(type: string, formatStr: string) {
  // @ts-ignore
  return infoRenderer.bind(this, type, formatStr);
}

export const primaryRenderers: RecordInfoRendererType = {
  auth: (value, record) => {
    const { success, endpoint_username } = record;
    const prefix = success ? 'Successful' : 'Failed';
    const user = endpoint_username || 'unknown user';
    const srcAddress = record.src_ip && record.src_ip.address;
    const srcCountry = record.src_ip && record.src_ip.country_name;

    return infoRenderer(
      'auth',
      `${prefix} authentication from ${
        srcAddress ? `IP ${srcAddress}` : 'unknown IP'
      } ${srcCountry ? `(${srcCountry}) ` : ''}for ${user}`,
      value,
      record
    );
  },
  dcerpc: renderInfoCol(
    'dcerpc',
    'Operation {operation} on named pipe {named_pipe} via endpoint {endpoint}'
  ),
  dhcp: renderInfoCol(
    'dhcp',
    'MAC Address {mac} assigned {assigned_ip.address} for {lease_time}'
  ),
  dns: (value, record) => {
    const qType = record.qtype || 'Unknown QTYPE';
    const query = record.query || 'Unknown query';
    const rCode = record.rcode;
    const rr = record.answer.rr;
    const ttl = record.answer.ttl;
    const suffix =
      rCode && rr && ttl
        ? ` (${rCode || 'Unknown RCODE'}: ${rr || 'Unknown'}; TTL: ${
            ttl || 'Unknown'
          })`
        : '';

    return infoRenderer('dns', `${qType}: ${query}${suffix}`, value, record);
  },
  endpoint: (value, record) =>
    infoRenderer('endpoint', record.message || 'None', value, record),
  file: (value, record) =>
    infoRenderer('file', 'Carved from {source}: {filename}', value, record),
  flow: renderInfoCol('flow', `{ip_proto}: {duration}`),
  ftp: renderInfoCol('ftp', '{user}: {command} {arg}'),
  http: renderInfoCol(
    'http',
    '{request.method} {request.url} ({response.status_code}; {response.response_body_len})'
  ),
  irc: renderInfoCol('irc', '{command}: {value}'),
  kerberos: (value, record) => {
    // Spell out request type if AS or TGS.
    let requestType = record.request_type;
    if (requestType === 'AS') {
      requestType = 'Authentication Service';
    } else if (requestType === 'TGS') {
      requestType = 'Ticket Granting Service';
    }

    return infoRenderer(
      'kerberos',
      `{client} to {service} (${requestType})`,
      value,
      record
    );
  },
  proxy: (value, record) => {
    const reqUrl = record.request.url;

    // Parse URL
    const url = reqUrl ? `${reqUrl} ` : '';
    const statusCode = record.response.status_code;
    const status = statusCode ? `${statusCode} ` : '';

    // Parse response body length
    const respLen = record.response.response_body_len;
    const size = typeof respLen === 'number' ? `${formatBytes(respLen)}` : '';
    const postfix = status && size ? `(${status}${size})` : '';

    return infoRenderer(
      'proxy',
      `{request.method} ${url}${postfix}`,
      value,
      record
    );
  },
  notice: renderInfoCol('notice', '{message}'),
  ntlm: (value, record) => {
    const result: string[] = [];
    if (record.domainname) {
      result.push(record.domainname);
    }
    if (record.username) {
      result.push(record.username);
    }

    return infoRenderer(
      'ntlm',
      `Authentication from ${
        result.join('\\') || 'unknown user'
      } on {hostname}`,
      value,
      record
    );
  },
  radius: renderInfoCol('radius', '{username} result: {result}'),
  rdp: (value, record) =>
    infoRenderer(
      'rdp',
      `Request Cookie: ${record.cookie || 'None'}`,
      value,
      record
    ),
  rfb: (value, record) => {
    const success = record.authentication_successful ? 'True' : 'False';

    return infoRenderer('rfb', `Auth successful: ${success}`, value, record);
  },
  sip: renderInfoCol('sip', '{request.method} From: {request.from}'),
  smb: (value, record) => {
    const { command, file } = record;
    const result: string[] = [];
    if (command && command.name) {
      result.push(command.name);
    }
    if (command && command.status) {
      result.push(command.status);
    }

    if (file.action) {
      result.push(file.action);
    }

    return infoRenderer('smb', result.join(' ') || 'None', value, record);
  },
  smtp: (value, record) => {
    const result: string[] = [];
    const helo = record.headers && record.headers.helo;
    if (helo) {
      result.push(helo);
    }
    const mailFrom = record.headers && record.headers.mailfrom;
    if (mailFrom) {
      result.push(`From: ${mailFrom}`);
    }

    const rcptTo = record.headers && record.headers.rcptto;
    if (rcptTo && rcptTo.length) {
      result.push(`To: ${rcptTo.join(', ')}`);
    }

    return infoRenderer(
      'smtp',
      result.length ? result.join(' ') : 'None',
      value,
      record
    );
  },
  snmp: (value, record) => {
    const count = record.get_requests + record.get_bulk_requests;
    const suffix = count !== 1 ? 's' : '';
    const result = [
      `${count.toLocaleString()} Request${suffix}`,
      `${record.get_responses.toLocaleString()} Response${suffix}`,
      `${record.set_requests.toLocaleString()} Set Request${suffix}`,
    ];

    return infoRenderer('snmp', result.join(', '), value, record);
  },
  socks: renderInfoCol(
    'socks',
    '{status} for request to {request.host.address}:{request_p} ({request.host.address.country_name})'
  ),
  ssh: renderInfoCol('ssh', '{client} to {server}'),
  threat: (value, record) => {
    const result: string[] = [];
    if (record.event_name) {
      result.push(`Event: ${record.event_name}`);
    }

    if (record.threat_value) {
      result.push(`Threat Value: ${record.threat_value}`);
    }

    return infoRenderer(
      'threat',
      result.length ? result.join(', ') : 'None',
      value,
      record
    );
  },
  tls: renderInfoCol('tls', '{server_name} {subject}'),
  vuln: (value, record) => {
    const name = record.vuln_name || 'Unknown name';
    const severityValue =
      typeof record.vuln_severity_value === 'number'
        ? ` (${record.vuln_severity_value})`
        : '';
    const destFqdn = record.dst_fqdn ? ` on ${record.dst_fqdn}` : '';

    return infoRenderer(
      'vuln',
      `${name}${severityValue}${destFqdn}`,
      value,
      record
    );
  },
  windows: (value, record) => {
    const result: string[] = [];
    const channel = record.channel;
    if (channel) {
      result.push(`${channel}:`);
    }

    const levelString = record.levelString;
    if (levelString) {
      result.push(levelString);
    }

    const eventId = record.event_id;
    if (eventId) {
      result.push(eventId);
    }

    return infoRenderer('windows', result.join(' '), value, record);
  },
  email: (value, record) => {
    const { sender, recipient } = record;
    const result: string[] = [];
    if (sender) {
      result.push(`Sender: ${sender}`);
    }
    if (recipient) {
      result.push(`Recipient: ${recipient}`);
    }

    return infoRenderer(
      'email',
      result.length ? result.join(', ') : 'None',
      value,
      record
    );
  },
};

export const secondaryRenderers: RecordInfoRendererType = {
  dns: (value, record) => {
    const { alexaDomainRank, entropy } = record;
    const { domain, domainName, query } = entropy;

    // Alexa domain rank
    const result: string[] = [];
    if (typeof alexaDomainRank === 'number') {
      result.push(`Alexa Rank: ${alexaDomainRank.toLocaleString()}`);
    }

    if (typeof domain === 'number') {
      result.push(`Domain Entropy: ${domain.toLocaleString()}`);
    }
    if (typeof domainName === 'number') {
      result.push(`Domain Name Entropy: ${domainName.toLocaleString()}`);
    }
    if (typeof query === 'number') {
      result.push(`Query Entropy: ${query.toLocaleString()}`);
    }

    return infoRenderer('dns', result.join('; '), value, record);
  },
  file: renderInfoCol('file', '{mime_type} {bytes.total}'),
  flow: renderInfoCol('flow', '{in_bytes} in, {out_bytes} out'),
  http: renderInfoCol('http', '{request.user_agent}'),
  irc: (value, record) => {
    const { user, nickname } = record;
    const result: string[] = [];
    if (user) {
      result.push(`User: ${user}`);
    }
    if (nickname) {
      result.push(`Nickname: ${nickname}`);
    }

    return infoRenderer('irc', result.join('; '), value, record);
  },
  proxy: renderInfoCol('proxy', '{request.user_agent}'),
  notice: renderInfoCol('notice', '{notice_type}'),
  ntlm: renderInfoCol('ntlm', '{status}'),
  radius: renderInfoCol('radius', '{connect_info} {mac} {remote_ip.address}'),
  rdp: (value, record) => {
    const result: string[] = [];
    const clientName = record.client && record.client.name;
    if (clientName) {
      result.push(`Hostname: ${clientName}`);
    }
    if (record.keyboard_layout) {
      result.push(`Keyboard: ${record.keyboard_layout}`);
    }

    return infoRenderer('rdp', result.join(', '), value, record);
  },
  rfb: renderInfoCol(
    'rfb',
    'Auth method: {authentication_method}; Desktop: {desktop_name}'
  ),
  sip: renderInfoCol('sip', '{headers.user_agent} To: {request.to}'),
  smb: renderInfoCol('smb', '{file.path}\\{file.filename}'),
  smtp: renderInfoCol('smtp', '{last_reply}'),
  snmp: renderInfoCol('snmp', '{display_string}'),
  ssh: (value, record) =>
    infoRenderer(
      'ssh',
      `Authentication ${record.auth_success ? 'successful' : 'failed'}`,
      value,
      record
    ),
  tls: renderInfoCol('tls', '{version} Issuer: {issuer}'),
  windows: (value, record) => {
    const result: string[] = [];
    if (record.endpoint_hostname) {
      result.push(record.endpoint_hostname);
    }
    if (record.endpoint_username) {
      result.push(record.endpoint_username);
    }

    return infoRenderer('windows', result.join(' '), value, record);
  },
};
